import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import Footer from "@/components/Footer";
import { Phone, EnvelopeSimple, MapPin, Clock, UsersThree, Storefront, WhatsappLogo, ArrowRight } from "@phosphor-icons/react";

import { WHATSAPP_LINK } from "@/lib/whatsapp";
import { setFloatingStack } from "@/lib/floatingStack";

function ContactInfoRow({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 py-5 border-b last:border-b-0" style={{ borderColor: "#2e2e2e" }}>
      <div
        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center mt-0.5"
        style={{ background: "rgba(255,122,0,0.15)" }}
      >
        <Icon size={18} weight="bold" style={{ color: "#FF7A00" }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold tracking-[0.18em] uppercase mb-0.5" style={{ color: "#666" }}>{label}</p>
        <p className="text-sm font-semibold text-white leading-snug break-all">{value}</p>
      </div>
    </div>
  );
  if (href) return <a href={href} className="block hover:opacity-80 transition-opacity">{inner}</a>;
  return inner;
}

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    userType: "Buyer",
    city: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focused, setFocused] = useState(null);

  useEffect(() => {
    setFloatingStack(1);
    return () => setFloatingStack(0);
  }, []);

  const onFieldChange = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const onSubmitMessage = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Contact enquiry from ${formData.fullName || "Website visitor"}`);
    const body = encodeURIComponent(
      [
        `Full Name: ${formData.fullName}`,
        `Mobile Number: ${formData.mobile}`,
        `Email: ${formData.email}`,
        `I am a: ${formData.userType}`,
        `City: ${formData.city || "-"}`,
        "",
        "Message:",
        formData.message,
      ].join("\n")
    );
    window.location.href = `mailto:Scaffoindia@gmail.com?subject=${subject}&body=${body}`;
    setIsSubmitted(true);
  };

  const inputClass = (key) =>
    `w-full bg-transparent text-white placeholder-neutral-600 text-sm border-b-2 outline-none py-2.5 px-0 transition-colors duration-200 rounded-none ${
      focused === key ? "border-[#FF7A00]" : "border-neutral-700"
    }`;

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar />

      {/* Dark hero banner */}
      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #111 0%, #1e1209 60%, #2d1a08 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(#FF7A00 1px, transparent 1px), linear-gradient(90deg, #FF7A00 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF7A00]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">

            {/* Left — headline */}
            <div className="py-16 sm:py-20 lg:py-24 lg:pr-16 border-b lg:border-b-0 lg:border-r" style={{ borderColor: "#2a2a2a" }}>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-6"
                style={{ background: "rgba(255,122,0,0.15)", color: "#FF7A00", border: "1px solid rgba(255,122,0,0.25)" }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF7A00] animate-pulse" />
                Get in touch
              </span>
              <h1
                className="font-black text-white leading-none tracking-tighter mb-5"
                style={{ fontSize: "clamp(2.8rem, 6vw, 4.5rem)", letterSpacing: "-0.03em" }}
              >
                Let's talk<br />
                <span style={{ color: "#FF7A00" }}>scaffolding.</span>
              </h1>
              <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-md mb-10">
                Whether you're sourcing materials or growing your supply business — our team is ready to help.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:+918920893912"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "#FF7A00" }}
                >
                  <Phone size={16} weight="bold" />
                  Call now
                </a>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: "#25D366", color: "#fff" }}
                >
                  <WhatsappLogo size={16} weight="bold" />
                  WhatsApp
                </a>
              </div>

              {/* Contact info list */}
              <div className="mt-12">
                <ContactInfoRow icon={Phone} label="Phone" value="+91 89208 93912" href="tel:+918920893912" />
                <ContactInfoRow icon={EnvelopeSimple} label="Email" value="Scaffoindia@gmail.com" href="mailto:Scaffoindia@gmail.com" />
                <ContactInfoRow icon={Clock} label="Hours" value="Mon – Sat, 10 AM – 7 PM" />
                <ContactInfoRow icon={MapPin} label="Office" value="Vipul Business Park, Sector 48, Gurugram, Haryana" />
              </div>
            </div>

            {/* Right — contact form */}
            <div className="py-16 sm:py-20 lg:py-24 lg:pl-16">
              <h2 className="text-xl font-black text-white mb-8 tracking-tight">Send a message</h2>

              {!isSubmitted ? (
                <form className="space-y-6" onSubmit={onSubmitMessage}>
                  <div>
                    <label className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2 block" style={{ color: "#888" }}>
                      Full Name *
                    </label>
                    <input
                      required
                      value={formData.fullName}
                      onChange={onFieldChange("fullName")}
                      onFocus={() => setFocused("fullName")}
                      onBlur={() => setFocused(null)}
                      placeholder="Your full name"
                      className={inputClass("fullName")}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2 block" style={{ color: "#888" }}>
                        Mobile *
                      </label>
                      <input
                        required
                        value={formData.mobile}
                        onChange={onFieldChange("mobile")}
                        onFocus={() => setFocused("mobile")}
                        onBlur={() => setFocused(null)}
                        placeholder="10-digit number"
                        className={inputClass("mobile")}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2 block" style={{ color: "#888" }}>
                        Email *
                      </label>
                      <input
                        required
                        type="email"
                        value={formData.email}
                        onChange={onFieldChange("email")}
                        onFocus={() => setFocused("email")}
                        onBlur={() => setFocused(null)}
                        placeholder="your@email.com"
                        className={inputClass("email")}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2 block" style={{ color: "#888" }}>
                        I am a
                      </label>
                      <select
                        value={formData.userType}
                        onChange={onFieldChange("userType")}
                        onFocus={() => setFocused("userType")}
                        onBlur={() => setFocused(null)}
                        className={inputClass("userType")}
                        style={{ appearance: "none" }}
                      >
                        <option value="Buyer" style={{ background: "#1a1a1a" }}>Buyer</option>
                        <option value="Supplier" style={{ background: "#1a1a1a" }}>Supplier</option>
                        <option value="Other" style={{ background: "#1a1a1a" }}>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2 block" style={{ color: "#888" }}>
                        City
                      </label>
                      <input
                        value={formData.city}
                        onChange={onFieldChange("city")}
                        onFocus={() => setFocused("city")}
                        onBlur={() => setFocused(null)}
                        placeholder="Your city"
                        className={inputClass("city")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2 block" style={{ color: "#888" }}>
                      Message *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={onFieldChange("message")}
                      onFocus={() => setFocused("message")}
                      onBlur={() => setFocused(null)}
                      placeholder="Tell us your requirement…"
                      className={`${inputClass("message")} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95 mt-2"
                    style={{ background: "#FF7A00" }}
                  >
                    Send message
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              ) : (
                <div
                  className="rounded-2xl p-7"
                  style={{ background: "rgba(255,122,0,0.1)", border: "1px solid rgba(255,122,0,0.2)" }}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg mb-4"
                    style={{ background: "rgba(255,122,0,0.2)", color: "#FF7A00" }}
                  >
                    ✓
                  </div>
                  <p className="text-base font-black text-white mb-2">Message sent!</p>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    Our team will get back to you within 24 hours. For urgent needs, call us at{" "}
                    <a href="tel:+918920893912" className="text-[#FF7A00] font-semibold">+91 89208 93912</a>.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to="/suppliers"
                      className="text-xs font-bold underline underline-offset-2"
                      style={{ color: "#FF7A00" }}
                    >
                      Explore suppliers
                    </Link>
                    <Link
                      to="/supplier/login"
                      className="text-xs font-bold underline underline-offset-2"
                      style={{ color: "#FF7A00" }}
                    >
                      Register as supplier
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom cards */}
      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-16">
        <p
          className="text-[10px] font-bold tracking-[0.2em] uppercase mb-6"
          style={{ color: "#FF7A00" }}
        >
          How can we help?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">

          {/* Buyers */}
          <div
            className="rounded-2xl p-7 flex flex-col"
            style={{ background: "#fff", border: "1px solid #E8E3D9" }}
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
              style={{ background: "rgba(255,122,0,0.1)" }}
            >
              <UsersThree size={22} weight="bold" style={{ color: "#FF7A00" }} />
            </div>
            <h3 className="text-base font-black text-neutral-900 mb-2">For buyers</h3>
            <p className="text-sm text-neutral-500 leading-relaxed flex-1">
              Looking to source scaffolding in Gurgaon? Share your requirement and we'll connect you with verified suppliers within 24 hours. Free.
            </p>
            <Link
              to="/materials"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-bold transition-colors hover:opacity-80"
              style={{ color: "#FF7A00" }}
            >
              Post requirement <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          {/* Suppliers */}
          <div
            className="rounded-2xl p-7 flex flex-col"
            style={{ background: "#fff", border: "1px solid #E8E3D9" }}
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
              style={{ background: "rgba(255,122,0,0.1)" }}
            >
              <Storefront size={22} weight="bold" style={{ color: "#FF7A00" }} />
            </div>
            <h3 className="text-base font-black text-neutral-900 mb-2">For suppliers</h3>
            <p className="text-sm text-neutral-500 leading-relaxed flex-1">
              Want quality leads for your scaffolding business? Register on our platform. Need help? Call or email us and we'll guide you through.
            </p>
            <Link
              to="/supplier/login"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-bold transition-colors hover:opacity-80"
              style={{ color: "#FF7A00" }}
            >
              Supplier registration <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          {/* Partnerships */}
          <div
            className="rounded-2xl p-7 flex flex-col"
            style={{ background: "#1a1a1a", border: "1px solid #2e2e2e" }}
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
              style={{ background: "rgba(255,122,0,0.15)" }}
            >
              <EnvelopeSimple size={22} weight="bold" style={{ color: "#FF7A00" }} />
            </div>
            <h3 className="text-base font-black text-white mb-2">Partnerships & media</h3>
            <p className="text-sm leading-relaxed flex-1" style={{ color: "#999" }}>
              Email us at{" "}
              <span className="text-white font-semibold">Scaffoindia@gmail.com</span>{" "}
              with subject line "Partnership" or "Media Enquiry."
            </p>
            <a
              href="mailto:Scaffoindia@gmail.com"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-bold transition-opacity hover:opacity-80"
              style={{ color: "#FF7A00" }}
            >
              Send email <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </main>

      {/* Floating WhatsApp */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all hover:scale-110 active:scale-95"
        style={{ background: "#25D366", color: "#fff", boxShadow: "0 4px 20px rgba(37,211,102,0.4)" }}
      >
        <WhatsappLogo size={28} weight="fill" />
      </a>

      <Footer />
    </div>
  );
}
